import { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RefreshCw, Plus, Trash2, Pause, Play, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);

export default function RecurringTransactions() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'expense', amount: '', category: '', description: '', frequency: 'monthly', day_of_month: '1' });
  const [categories, setCategories] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/recurring-transactions`, { withCredentials: true }).then(r => setItems(r.data));
    axios.get(`${API}/categories`, { withCredentials: true }).then(r => setCategories(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.amount || !form.category) return toast.error('Completa monto y categoría');
    await axios.post(`${API}/recurring-transactions`, form, { withCredentials: true });
    toast.success('Transacción recurrente creada');
    setShowAdd(false);
    setForm({ type: 'expense', amount: '', category: '', description: '', frequency: 'monthly', day_of_month: '1' });
    load();
  };

  const toggle = async (id, active) => {
    await axios.put(`${API}/recurring-transactions/${id}`, { is_active: !active }, { withCredentials: true });
    load();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/recurring-transactions/${id}`, { withCredentials: true });
    toast.success('Eliminada');
    load();
  };

  const freqLabel = { monthly: 'Mensual', biweekly: 'Quincenal', weekly: 'Semanal' };

  return (
    <Card className="bg-[#1a2332] border-[#2a3444]" data-testid="recurring-transactions">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
          <RefreshCw className="w-5 h-5 text-[#D4AF37]" />
          Transacciones Recurrentes
        </CardTitle>
        <Button size="sm" className="btn-gold" onClick={() => setShowAdd(!showAdd)} data-testid="add-recurring-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {showAdd && (
          <div className="bg-[#141b2d] border border-[#D4AF37]/30 rounded-lg p-4 space-y-3" data-testid="recurring-form">
            <div className="grid grid-cols-2 gap-3">
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="bg-[#1a2332] border border-[#2a3444] text-white text-sm rounded-lg px-3 py-2">
                <option value="expense">Gasto</option>
                <option value="income">Ingreso</option>
              </select>
              <Input type="number" placeholder="Monto" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="bg-[#1a2332] border-[#2a3444] text-white text-sm" />
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="bg-[#1a2332] border border-[#2a3444] text-white text-sm rounded-lg px-3 py-2">
                <option value="">Categoría...</option>
                {categories.filter(c => c.type === form.type).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})} className="bg-[#1a2332] border border-[#2a3444] text-white text-sm rounded-lg px-3 py-2">
                <option value="monthly">Mensual</option>
                <option value="biweekly">Quincenal</option>
                <option value="weekly">Semanal</option>
              </select>
              <Input type="number" min="1" max="31" placeholder="Día" value={form.day_of_month} onChange={e => setForm({...form, day_of_month: e.target.value})} className="bg-[#1a2332] border-[#2a3444] text-white text-sm" />
              <Input placeholder="Descripción" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#1a2332] border-[#2a3444] text-white text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="btn-gold" onClick={handleAdd}>Guardar</Button>
              <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => setShowAdd(false)}>Cancelar</Button>
            </div>
          </div>
        )}
        {items.length > 0 ? (
          <div className="space-y-2">
            {items.map(item => (
              <div key={item.recurring_id} className={`flex items-center gap-3 p-3 rounded-lg border group ${item.is_active ? 'bg-[#141b2d] border-[#2a3444]' : 'bg-[#141b2d]/50 border-[#2a3444]/50 opacity-60'}`} data-testid={`recurring-${item.recurring_id}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.type === 'income' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  {item.type === 'income' ? <ArrowUpRight className="w-4 h-4 text-green-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{item.category}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#2a3444] text-gray-400">{freqLabel[item.frequency]} — Día {item.day_of_month}</span>
                  </div>
                  {item.description && <p className="text-xs text-gray-500 truncate">{item.description}</p>}
                </div>
                <span className={`font-mono text-sm shrink-0 ${item.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>{fmt(item.amount)}</span>
                <button onClick={() => toggle(item.recurring_id, item.is_active)} className="text-gray-500 hover:text-[#D4AF37] opacity-0 group-hover:opacity-100">
                  {item.is_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => handleDelete(item.recurring_id)} className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : !showAdd && (
          <div className="py-4 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-30 text-gray-600" />
            <p className="text-sm text-gray-600">Sin transacciones recurrentes</p>
            <p className="text-xs text-gray-700 mt-1">Se generan automáticamente según la frecuencia configurada</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
